/**
 * AssetManagerDrawer — the asset-library drawer plus its "add to canvas"
 * import flow.
 *
 * Extracted from <ChatSidebarDock> when that component was deleted: its
 * three buttons moved to <CanvasBottomPalette>, but it also owned this
 * <ReferenceSelector> mount, so the drawer needed a home that outlives it.
 * Both the palette's Library button and <SimplifiedWorkspace>'s
 * "open library" affordance drive it through the same `assetManagerOpen`
 * state in WorkspacePageV2.
 *
 * Behaviour is unchanged from the ChatSidebarDock version — same test ids,
 * same `canvasToolbox.*` i18n keys, same per-reference import loop.
 */

import { memo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useReactFlow } from 'reactflow'
import { toast } from 'sonner'
import { ReferenceSelector } from './nodes/shared/ReferenceSelector'
import { createNode, type RawNodeDataRecord } from './nodeRegistry'
import { stripDirectAssetInputPortWrites } from './assetPortGuards'
import { authenticatedFetch, getConfiguredApiBase } from '../../api/client'
import { createNadDebug } from '../../utils/debugLog'
import { noteNodeActions } from '../../analytics/nodeActionAnalytics'
import type { ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../hooks/useYCanvas'

const libraryImportLog = createNadDebug('dw:library-import:asset-manager')

interface ImportReferenceResponse {
  node_type: string
  node_data: RawNodeDataRecord
  assets_copied: number
}

export interface AssetManagerDrawerProps {
  projectId: string
  isOpen: boolean
  onClose: () => void
  onAddNode: (node: ReturnType<typeof createNode>, explicitData?: RawNodeDataRecord) => void
}

export const AssetManagerDrawer = memo(function AssetManagerDrawer({
  projectId,
  isOpen,
  onClose,
  onAddNode,
}: AssetManagerDrawerProps) {
  const { t } = useTranslation('canvas')
  const { getViewport, getNodes } = useReactFlow<
    ReactFlowCanvasNodeData,
    ReactFlowCanvasEdgeData
  >()

  // Reference point for the viewport-centre math below. ChatSidebarDock used
  // its own button row; this sits outside `.react-flow` just the same, so the
  // lookup falls through to window width exactly as it did before.
  const anchorRef = useRef<HTMLDivElement>(null)

  const handleDeleteFromLibrary = useCallback(
    async (nodeId: string) => {
      try {
        const resp = await authenticatedFetch(
          `${getConfiguredApiBase()}/library/nodes/${nodeId}`,
          { method: 'DELETE' },
        )
        if (!resp.ok) {
          const err = await resp.json().catch(() => null)
          throw new Error(err?.detail || resp.statusText)
        }
        toast.success(t('canvasToolbox.removedFromLibrary'))
      } catch (err) {
        toast.error(
          t('canvasToolbox.failedToDelete', {
            message: err instanceof Error ? err.message : 'Unknown error',
          }),
        )
      }
    },
    [t],
  )

  const handleAddToCanvas = useCallback(
    async (
      references: {
        nodeId: string
        nodeType: string
        label: string
        assetPath: string
        mediaType: string
      }[],
    ) => {
      if (references.length === 0) return
      onClose()

      const toastId = toast.loading(t('canvasToolbox.addingToCanvas'))
      const vp = getViewport()
      const rfContainer = anchorRef.current?.closest('.react-flow')
      const cw = rfContainer ? rfContainer.getBoundingClientRect().width : window.innerWidth
      const baseCx = (-vp.x + cw / 2) / vp.zoom
      const baseCy = (-vp.y + window.innerHeight / 2) / vp.zoom

      const apiBase = getConfiguredApiBase()
      let added = 0

      for (let i = 0; i < references.length; i++) {
        const ref = references[i]
        try {
          const resp = await authenticatedFetch(
            `${apiBase}/library/nodes/${ref.nodeId}/import`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ target_project_id: projectId }),
            },
          )
          if (!resp.ok) {
            const err = await resp.json().catch(() => null)
            console.error(
              `[AssetManager] Import failed for ${ref.nodeId}:`,
              err?.detail || resp.statusText,
            )
            continue
          }
          const { node_type, node_data }: ImportReferenceResponse = await resp.json()
          const { data: importNodeData, strippedFields } = stripDirectAssetInputPortWrites(
            node_type,
            node_data,
          )
          libraryImportLog({
            trace_id: `library-import:${node_type}`,
            event: 'import-response-received',
            libraryNodeId: ref.nodeId,
            node_type,
            stripped_edge_backed_inputs: strippedFields,
          })
          const position = { x: baseCx + i * 60, y: baseCy + i * 60 }
          const newNode = createNode(node_type, position, importNodeData)
          noteNodeActions([newNode.id], 'add', 'asset_library')
          onAddNode(newNode, importNodeData)
          added++
        } catch (error) {
          console.error(`[AssetManager] Import error for ${ref.nodeId}:`, error)
        }
      }

      if (added > 0) {
        toast.success(
          added === 1
            ? t('canvasToolbox.addedToCanvas')
            : t('canvasToolbox.addedCountToCanvas', { count: added }),
          { id: toastId },
        )
      } else {
        toast.error(t('canvasToolbox.failedToAddToCanvas'), { id: toastId })
      }
    },
    [getViewport, onAddNode, onClose, projectId, t],
  )

  return (
    <>
      <div ref={anchorRef} className="hidden" aria-hidden="true" />
      <ReferenceSelector
        isOpen={isOpen}
        onClose={onClose}
        onSave={() => {}}
        excludeNodeId=""
        canvasNodes={getNodes()}
        mode="asset-manager"
        onAddToCanvas={handleAddToCanvas}
        onDeleteFromLibrary={handleDeleteFromLibrary}
        maxReferences={99}
      />
    </>
  )
})
